<template>
  <div
    class="notification-card border-main-dark-20 pointer-events-auto relative flex gap-x-3 rounded-lg border bg-white px-4 py-3"
    :role="props.type === 'error' ? 'alert' : 'status'"
    @mouseenter="toggleTimer(true)"
    @mouseleave="toggleTimer(false)"
  >
    <p class="text-14 min-w-0 grow leading-snug" :class="messageClass">
      {{ message }}
    </p>
    <button
      type="button"
      class="text-main-dark-40 hover:bg-main-dark-05 hover:text-main-dark-80 shrink-0 rounded p-0.5 transition"
      aria-label="Dismiss notification"
      @click="dismiss"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
      >
        <path
          d="M12.3457 2.84127C12.5702 2.61671 12.9336 2.61671 13.1582 2.84127C13.3827 3.06582 13.3827 3.42921 13.1582 3.65377L8.81246 7.99947L13.1582 12.3461L13.2324 12.436C13.3797 12.6592 13.3546 12.9622 13.1582 13.1586C12.9617 13.3551 12.6587 13.3802 12.4355 13.2329L12.3457 13.1586L7.99898 8.81294L3.65328 13.1586C3.42873 13.3832 3.06533 13.3832 2.84078 13.1586C2.61623 12.9341 2.61623 12.5707 2.84078 12.3461L7.1855 7.99947L2.84078 3.65377C2.61623 3.42921 2.61623 3.06582 2.84078 2.84127C3.06533 2.61671 3.42873 2.61671 3.65328 2.84127L7.99898 7.18599L12.3457 2.84127Z"
          fill="currentColor"
        />
      </svg>
    </button>
  </div>
</template>

<script setup lang="ts">
import Timer from './timer';
import { computed, onBeforeUnmount, onMounted } from 'vue';
import { dismissToast, type AppNotificationType } from './index';

const props = defineProps<{
  id: string;
  message: string;
  type: AppNotificationType;
  durationMs: number;
}>();

const messageClasses: Record<AppNotificationType, string> = {
  info: 'text-main-dark-100',
  success: 'text-main-dark-100',
  error: 'text-danger-red',
  warning: 'text-main-dark-100',
};

const messageClass = computed(() => messageClasses[props.type]);

let timer: Timer<void> | null = null;

function dismiss(): void {
  if (timer) {
    timer.stop();
    timer = null;
  }
  dismissToast(props.id);
}

function toggleTimer(pause: boolean): void {
  if (!timer) {
    return;
  }
  pause ? timer.pause() : timer.resume();
}

onMounted(() => {
  if (props.durationMs > 0) {
    timer = new Timer(dismiss, props.durationMs);
  }
});

onBeforeUnmount(() => {
  if (timer) {
    timer.stop();
    timer = null;
  }
});
</script>

<style scoped>
.notification-card {
  width: min(350px, calc(100vw - 2rem));
  max-width: 350px;
  box-shadow:
    0px 6px 12px 0px rgba(0, 0, 0, 0.06),
    0px 8px 8px 0px rgba(0, 0, 0, 0.09),
    0px 0px 2px 0px rgba(0, 0, 0, 0.06);
}
</style>
